import React, { useState, useMemo } from 'react';
import { FaSearch, FaEdit, FaChartBar, FaFileAlt } from 'react-icons/fa';
import { Form } from 'react-bootstrap';

const Dashboard = ({
    parameterContent,
    onSelectParameter,
    version,
    allVersions,
    setEditVersion
}) => {
    const [searchTerm, setSearchTerm] = useState('');

    const parameters = Object.keys(parameterContent || {}).sort();

    const filteredParameters = useMemo(() => {
        if (!searchTerm) return parameters;
        return parameters.filter(p =>
            p.toLowerCase().includes(searchTerm.toLowerCase())
        );
    }, [parameters, searchTerm]);

    const hasTextContent = (textObj) => {
        if (!textObj) return false;
        return Object.values(textObj).some(val => val && String(val).trim() !== '');
    };

    const hasTipsContent = (tipsObj) => {
        if (!tipsObj) return false;
        if (tipsObj.tipsHeader && Object.values(tipsObj.tipsHeader).some(val => val && String(val).trim() !== '')) return true;
        if (tipsObj.tipsSvgText && Object.values(tipsObj.tipsSvgText).some(arr => Array.isArray(arr) && arr.some(val => val && String(val).trim() !== ''))) return true;
        return false;
    };

    const hasCausesContent = (causesObj) => {
        if (!causesObj) return false;
        return Object.values(causesObj).some(cause => {
            if (!cause) return false;
            if (cause.heading && Object.values(cause.heading).some(val => val && String(val).trim() !== '')) return true;
            if (cause.description && Object.values(cause.description).some(val => val && String(val).trim() !== '')) return true;
            if (Array.isArray(cause.list) && cause.list.some(item => item && item.text && Object.values(item.text).some(val => val && String(val).trim() !== ''))) return true;
            return false;
        });
    };

    const hasAdditionalSections = (sectionsArray) => {
        if (!Array.isArray(sectionsArray)) return false;
        return sectionsArray.some(sec => {
            if (sec.heading && Object.values(sec.heading).some(val => val && String(val).trim() !== '')) return true;
            if (sec.description && Object.values(sec.description).some(val => val && String(val).trim() !== '')) return true;
            if (Array.isArray(sec.list) && sec.list.some(item => item && item.text && Object.values(item.text).some(val => val && String(val).trim() !== ''))) return true;
            return false;
        });
    };

    const hasVizAppContent = (vizappObj) => {
        if (!vizappObj) return false;
        return Object.values(vizappObj).some(arr => 
            Array.isArray(arr) && arr.some(item => item && item.text && Object.values(item.text).some(val => val && String(val).trim() !== ''))
        );
    };

    const hasDynamicText = (dynamicTextObj) => {
        if (!dynamicTextObj) return false;
        return Object.values(dynamicTextObj).some(val => val && String(val).trim() !== '');
    };

    return (
        <div className="pcr-dashboard">
            <header className="pcr-welcome-header">
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', textAlign: 'left' }}>
                    <div>
                        <h1 className="pcr-welcome-title" style={{ marginTop: 0 }}>Parameter Content Manager</h1>
                        <p className="pcr-welcome-subtitle" style={{ marginBottom: 0 }}>
                            Select a parameter to start editing its content, tips, and configurations.
                        </p>
                    </div>

                    <div className="pcr-version-control" style={{
                        background: 'white',
                        padding: '0.75rem 1rem',
                        borderRadius: '12px',
                        boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.05)',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.75rem'
                    }}>
                        <label htmlFor="version-select" style={{ fontWeight: 600, fontSize: '0.9rem', color: '#64748b' }}>Version:</label>
                        <Form.Select
                            id="version-select"
                            value={version || ''}
                            onChange={(e) => setEditVersion(e.target.value)}
                            style={{
                                width: '180px',
                                border: '1px solid #e2e8f0',
                                borderRadius: '8px',
                                fontSize: '0.9rem',
                                padding: '0.4rem 0.75rem'
                            }}
                        >
                            {allVersions?.map((v) => (
                                <option key={v} value={v === 'live' ? '' : v}>
                                    {v === 'live' ? 'Live Production' : v}
                                </option>
                            ))}
                        </Form.Select>
                    </div>
                </div>
            </header>

            <div className="pcr-search-container">
                <FaSearch style={{ position: 'absolute', left: '1rem', top: '1.1rem', color: '#94a3b8' }} />
                <input
                    type="text"
                    className="pcr-search-input"
                    placeholder="Search parameters (e.g., Hemoglobin, Iron)..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    autoFocus
                />
            </div>

            {/* Quick Stats / Summary (Optional Placeholder) */}
            {/* <div style={{ marginBottom: '2rem', display: 'flex', gap: '1rem', justifyContent: 'center' }}>
                <span style={{ fontSize: '0.9rem', color: '#64748b' }}>
                    <strong>{parameters.length}</strong> Total Parameters
                </span>
            </div> */}

            <div className="pcr-grid-3">
                {filteredParameters.map(param => (
                    <div
                        key={param}
                        className="pcr-card"
                        role="button"
                        tabIndex={0}
                        onClick={() => onSelectParameter(param)}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                                e.preventDefault();
                                onSelectParameter(param);
                            }
                        }}
                    >
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
                            <h3 style={{ fontSize: '1.1rem', fontWeight: 600, color: '#0f172a', margin: 0 }}>
                                {param}
                            </h3>
                            <FaEdit style={{ color: '#cbd5e1' }} aria-hidden="true" />
                        </div>
                        <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                            {/* Simple badges showing what content exists */}
                            {(() => {
                                const showText = parameterContent[param]?.text && hasTextContent(parameterContent[param].text);
                                const showDynamicText = parameterContent[param]?.dynamicText && hasDynamicText(parameterContent[param].dynamicText);
                                const showTips = parameterContent[param]?.tips && hasTipsContent(parameterContent[param].tips);
                                const showCauses = parameterContent[param]?.causes && hasCausesContent(parameterContent[param].causes);
                                const showAdditional = parameterContent[param]?.additionalSections && hasAdditionalSections(parameterContent[param].additionalSections);
                                const showVizApp = parameterContent[param]?.vizapp && hasVizAppContent(parameterContent[param].vizapp);

                                if (!showText && !showDynamicText && !showTips && !showCauses && !showAdditional && !showVizApp) {
                                    return <span style={{ fontSize: '0.75rem', padding: '2px 6px', background: '#f1f5f9', color: '#64748b', borderRadius: '4px' }}>No Data Available</span>;
                                }

                                return (
                                    <>
                                        {showText && (
                                            <span style={{ fontSize: '0.75rem', padding: '2px 6px', background: '#dcfce7', color: '#166534', borderRadius: '4px' }}>Text</span>
                                        )}
                                        {showDynamicText && (
                                            <span style={{ fontSize: '0.75rem', padding: '2px 6px', background: '#ecfccb', color: '#3f6212', borderRadius: '4px' }}>Dynamic Text</span>
                                        )}
                                        {showCauses && (
                                            <span style={{ fontSize: '0.75rem', padding: '2px 6px', background: '#fef3c7', color: '#92400e', borderRadius: '4px' }}>Causes</span>
                                        )}
                                        {showTips && (
                                            <span style={{ fontSize: '0.75rem', padding: '2px 6px', background: '#e0f2fe', color: '#075985', borderRadius: '4px' }}>Tips</span>
                                        )}
                                        {showAdditional && (
                                            <span style={{ fontSize: '0.75rem', padding: '2px 6px', background: '#ede9fe', color: '#5b21b6', borderRadius: '4px' }}>Additional Sections</span>
                                        )}
                                        {showVizApp && (
                                            <span style={{ fontSize: '0.75rem', padding: '2px 6px', background: '#fce7f3', color: '#9d174d', borderRadius: '4px' }}>VizApp</span>
                                        )}
                                    </>
                                );
                            })()}
                        </div>
                    </div>
                ))}

                {filteredParameters.length === 0 && (
                    <div style={{ gridColumn: '1 / -1', textAlign: 'center', padding: '3rem', color: '#94a3b8' }}>
                        No parameters found matching "{searchTerm}"
                    </div>
                )}
            </div>
        </div>
    );
};

export default Dashboard;
